import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import type { AlertColor } from '@mui/material/Alert';

/**
 * Lightweight global toast. There was no snackbar/notification primitive in the
 * app — recommendation actions (add-to-task, add-to-studio) silently succeeded,
 * which read as "nothing happened". `useToast().show(msg)` surfaces a brief
 * confirmation from anywhere.
 */
interface ToastState {
  message: string;
  severity: AlertColor;
  /** Optional CTA (e.g. "Voir" → navigate to Tasks / Studio). */
  action?: { label: string; onClick: () => void };
  key: number;
}

interface ToastContextValue {
  show: (message: string, opts?: { severity?: AlertColor; action?: ToastState['action'] }) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toast, setToast] = useState<ToastState | null>(null);

  const show = useCallback<ToastContextValue['show']>((message, opts) => {
    setToast({
      message,
      severity: opts?.severity || 'success',
      action: opts?.action,
      key: Date.now(),
    });
  }, []);

  const handleClose = useCallback((_?: unknown, reason?: string) => {
    if (reason === 'clickaway') return;
    setToast(null);
  }, []);

  const value = useMemo(() => ({ show }), [show]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <Snackbar
        key={toast?.key}
        open={!!toast}
        autoHideDuration={4000}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        {toast ? (
          <Alert
            onClose={handleClose}
            severity={toast.severity}
            variant="filled"
            sx={{ boxShadow: 4, alignItems: 'center' }}
            action={
              toast.action ? (
                <button
                  onClick={() => {
                    toast.action?.onClick();
                    setToast(null);
                  }}
                  style={{
                    background: 'rgba(255,255,255,0.2)',
                    border: 'none',
                    color: '#fff',
                    fontWeight: 700,
                    borderRadius: 6,
                    padding: '4px 10px',
                    cursor: 'pointer',
                    fontSize: 13,
                  }}
                >
                  {toast.action.label}
                </button>
              ) : undefined
            }
          >
            {toast.message}
          </Alert>
        ) : (
          <span />
        )}
      </Snackbar>
    </ToastContext.Provider>
  );
}

/**
 * Access the toast. Returns a no-op `show` when no provider is mounted so a
 * component used outside the tree (e.g. in isolation) never crashes.
 */
export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  return ctx || { show: () => {} };
}
